// src/components/ChatEconomy.js
//
// The fan-side of the XPass chat economy, one import:
//
//   useChatGate(creatorId)  — mirrors the server's gate_status
//   GiftBar                 — 🌹 $15 · 🔮 $30 · 💎 $60 · 👑 $150 above the composer,
//                             paid from the Lunexx balance (unlock-message gift_buy);
//                             prices and availability come from the server catalog
//                             (credit_gift_catalog), SKUS only carries the styling
//   GiftBubble              — renders type 'gift' messages
//   ChatPaywall             — the post-taste wall for BOTH creator types:
//                             Own the founding pass, or rent a Chat Pass
//                             ($10 / +300 turns → platform). Whether the
//                             rent half shows is the server's call.
//   HumanChatGate           — deprecated alias for ChatPaywall
//
// P8: the "pass" is a FOUNDING CARD once MARKETS_ENABLED is false. The bar
// reads status.card (seriesId / priceWei / minted / maxSupply / soldOut /
// dropLive) served by unlock-message's gate_status.
//
// Gifts buy ATTENTION, never permission — the composer stays usable.
// AUTH FOOTGUN: src/context/AuthContext.js (SIWE, singular).

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, MessageSquareLock, Sparkles } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { MARKETS_ENABLED, PAYMENTS_ENABLED, PAYMENTS_UNAVAILABLE_MESSAGE } from '../config/flags';
import { supabase } from '../lib/supabase';
import { useSiweAuth } from '../context/AuthContext';
import { sendBaseSepoliaPayment } from '../lib/ensureChain';
import { classifyTxError, TopUpCta } from '../lib/txErrors';
import { functionsError } from '../lib/credits';
import { AddFundsButton, useAddFunds } from './credits/AddFunds';
import { balanceChanged } from '../hooks/useBalanceSummary';

const PLATFORM_WALLET = process.env.REACT_APP_PLATFORM_WALLET; // chat passes pay HERE

async function bridge(authToken, body) {
  const { data, error } = await supabase.functions.invoke('unlock-message', {
    body, headers: { 'X-Lunexx-Auth': `Bearer ${authToken}` },
  });
  if (error) {
    let message = error.message || 'request failed';
    if (error.context?.json) {
      try { const d = await error.context.json(); if (d?.error) message = d.error; } catch { /* noop */ }
    }
    throw new Error(message);
  }
  return data;
}

/** unlock-message call that keeps the server's error code (gift_buy maps
 *  insufficient_funds to an Add-funds link). */
async function giftCall(authToken, body) {
  const { data, error } = await supabase.functions.invoke('unlock-message', {
    body, headers: { 'X-Lunexx-Auth': `Bearer ${authToken}` },
  });
  if (error) {
    const e = await functionsError(error);
    if (!e.code) e.message = 'The gift could not be sent.';
    throw e;
  }
  return data;
}

/** Quote -> pay -> verify, with the tx_not_found backoff.
 *
 *  EXPORTED so RequestGate's Chat Pass button uses this exact loop. A
 *  second copy would eventually lose the retry, and losing the retry means
 *  a fan's money is on-chain while their unlock silently never lands —
 *  the settlement path answers tx_not_found with retry:true for the first
 *  few seconds by design. */
export async function payThenVerify(
  ensureAuth,
  toWallet,
  quoteEth,
  verifyBody,
) {
  if (typeof ensureAuth !== 'function') {
    throw new Error(
      `Invalid ensureAuth: expected function, received ${typeof ensureAuth}`,
    );
  }
  // Never open a wallet for a payment the server will refuse.
  if (!PAYMENTS_ENABLED) throw new Error(PAYMENTS_UNAVAILABLE_MESSAGE);

  const token = await ensureAuth();

  const txHash = await sendBaseSepoliaPayment({
    to: toWallet,
    amountEth: quoteEth,
  });

  for (let attempt = 0; ; attempt++) {
    try {
      return await bridge(token, {
        ...verifyBody,
        txHash,
        amountEth: quoteEth,
      });
    } catch (error) {
      if (
        !/tx_not_found/.test(error?.message ?? '') ||
        attempt >= 5
      ) {
        throw error;
      }

      await new Promise((resolve) =>
        setTimeout(resolve, 2500),
      );
    }
  }
}

// ---------------------------------------------------------------------
export function useChatGate(creatorId, enabled = true) {
  const { ensureAuth } = useSiweAuth();
  const [status, setStatus] = useState(null); // gate_status payload

  const refresh = useCallback(async () => {
    if (!creatorId) return;
    try {
      const token = await ensureAuth();
      setStatus(await bridge(token, { action: 'gate_status', creatorId }));
    } catch (e) { console.warn('[gate] status failed:', e); }
  }, [creatorId, ensureAuth]);

  useEffect(() => {
    if (enabled && creatorId) refresh();
  }, [enabled, creatorId, refresh]);

  return { status, refresh };
}

// ---------------------------------------------------------------------
const SKUS = [
  {
    sku: 'rose',
    emoji: '🌹',
    usd: 15,
    accent: '#FB7185',
    accentRgb: '251,113,133',
    rotation: -2,
  },
  {
    sku: 'crystal_orb',
    emoji: '🔮',
    usd: 30,
    accent: '#C084FC',
    accentRgb: '192,132,252',
    rotation: 2,
  },
  {
    sku: 'diamond',
    emoji: '💎',
    usd: 60,
    accent: '#67E8F9',
    accentRgb: '103,232,249',
    rotation: -2,
  },
  {
    sku: 'crown',
    emoji: '👑',
    usd: 150,
    accent: '#FACC15',
    accentRgb: '250,204,21',
    rotation: 2,
  },
];
const BURST_PARTICLES = [
  { x: -54, y: -38, delay: 0 },
  { x: -30, y: -66, delay: 0.04 },
  { x: 0, y: -78, delay: 0.08 },
  { x: 32, y: -64, delay: 0.12 },
  { x: 56, y: -34, delay: 0.16 },
  { x: -46, y: 0, delay: 0.1 },
  { x: 48, y: 2, delay: 0.14 },
];

// Render-only entries for skus the server once sold but the bar no
// longer offers — old messages must keep rendering as gift bubbles.
const LEGACY_RENDER_SKUS = [
  {
    sku: 'chocolate',
    emoji: '🍫',
    usd: 5,
    accent: '#D6A26B',
    accentRgb: '214,162,107',
    rotation: 0,
  },
];
const ALL_RENDER_SKUS = [...SKUS, ...LEGACY_RENDER_SKUS];

export const resolveGiftMessage = (message) => {
  const metadata =
    message?.metadata ??
    message?.meta ??
    {};

  const explicitSku =
    message?.gift_sku ??
    message?.giftSku ??
    message?.sku ??
    metadata?.gift_sku ??
    metadata?.giftSku ??
    metadata?.sku ??
    null;

  const content = String(
    message?.content ?? '',
  ).trim();

  const messageType =
    message?.type ??
    message?.message_type ??
    message?.messageType ??
    null;

  // GIFT-NESS comes from the message TYPE (the server always stamps
  // type 'gift') or an explicit sku — NEVER from emoji sniffing. The
  // old emoji-based detection turned any ordinary text containing 💎
  // or 🌹 into a fake "$60 activated" gift bubble.
  const looksLikeGift =
    messageType === 'gift' ||
    Boolean(explicitSku);
  if (!looksLikeGift) {
    return null;
  }

  // Emoji matching only picks WHICH sku a confirmed gift is.
  const gift =
    ALL_RENDER_SKUS.find(
      (item) => item.sku === explicitSku,
    ) ??
    ALL_RENDER_SKUS.find(
      (item) => content.includes(item.emoji),
    ) ??
    null;
  if (!gift) {
    return null;
  }

  // Price precedence: explicit fields → the "· $N" the server stamps
  // into gift content (survives catalog repricing) → catalog default.
  const contentUsd = content.match(/\$\s?(\d+(?:\.\d+)?)/)?.[1];
  const rawUsd =
    message?.gift_usd ??
    message?.giftUsd ??
    message?.amount_usd ??
    message?.amountUsd ??
    message?.usd ??
    metadata?.gift_usd ??
    metadata?.giftUsd ??
    metadata?.amount_usd ??
    metadata?.amountUsd ??
    metadata?.usd ??
    contentUsd ??
    gift.usd;

  return {
    ...gift,
    usd: Number.isFinite(Number(rawUsd))
      ? Number(rawUsd)
      : gift.usd,
  };
};

function GiftBurst({ gift }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pointer-events-none absolute bottom-full left-1/2 z-50 h-28 w-40 -translate-x-1/2"
    >
      {/* Expanding energy rings */}
      {[0, 1].map((ring) => (
        <motion.div
          key={ring}
          initial={{
            opacity: 0.8,
            scale: 0.35,
          }}
          animate={{
            opacity: 0,
            scale: ring === 0 ? 1.5 : 2,
          }}
          transition={{
            duration: 0.8,
            delay: ring * 0.12,
            ease: 'easeOut',
          }}
          className="absolute left-1/2 top-1/2 h-16 w-16 -translate-x-1/2 -translate-y-1/2 rounded-full border"
          style={{
            borderColor: `rgba(${gift.accentRgb}, 0.55)`,
            boxShadow: `0 0 30px rgba(${gift.accentRgb}, 0.35)`,
          }}
        />
      ))}

      {/* Main launched gift */}
      <motion.div
        initial={{
          opacity: 0,
          y: 35,
          scale: 0.45,
          rotate: -12,
        }}
        animate={{
          opacity: [0, 1, 1, 0],
          y: [35, -12, -25, -42],
          scale: [0.45, 1.25, 1.08, 0.85],
          rotate: [-12, 8, -4, 0],
        }}
        transition={{
          duration: 1.1,
          times: [0, 0.35, 0.72, 1],
          ease: 'easeOut',
        }}
        className="absolute left-1/2 top-1/2 grid h-16 w-16 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-[22px] border bg-black/60 text-[38px] backdrop-blur-xl"
        style={{
          borderColor: `rgba(${gift.accentRgb}, 0.38)`,
          boxShadow: `
            0 0 25px rgba(${gift.accentRgb}, 0.55),
            0 0 60px rgba(${gift.accentRgb}, 0.25),
            inset 0 1px 0 rgba(255,255,255,0.18)
          `,
        }}
      >
        {gift.emoji}
      </motion.div>

      {/* Energy particles */}
      {BURST_PARTICLES.map((particle, index) => (
        <motion.span
          key={index}
          initial={{
            opacity: 0,
            x: 0,
            y: 0,
            scale: 0,
          }}
          animate={{
            opacity: [0, 1, 0],
            x: particle.x,
            y: particle.y,
            scale: [0, 1, 0.25],
          }}
          transition={{
            duration: 0.65,
            delay: particle.delay,
            ease: 'easeOut',
          }}
          className="absolute left-1/2 top-1/2 h-1.5 w-1.5 rounded-full"
          style={{
            backgroundColor: gift.accent,
            boxShadow: `0 0 12px rgba(${gift.accentRgb}, 0.95)`,
          }}
        />
      ))}
    </motion.div>
  );
}


export function GiftBar({
  conversationId,
  onSent,
}) {
  const { ensureAuth, isAuthenticated } = useSiweAuth();
  const { openAddFunds } = useAddFunds();

  const [sending, setSending] = useState(null);
  const [burst, setBurst] = useState(null);
  const [err, setErr] = useState(null);
  // Low-balance failures open the Add Funds modal (and keep an "Add funds"
  // button); the gift is never re-sent by itself. Holds the refusal.
  const [needsFunds, setNeedsFunds] = useState(null);
  // Server truth: whether gifts can be bought now, and their prices.
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    let live = true;
    if (!isAuthenticated) { setCatalog(null); return undefined; }
    (async () => {
      try {
        const token = await ensureAuth();
        const c = await giftCall(token, { action: 'gift_catalog' });
        if (live) setCatalog(c);
      } catch { if (live) setCatalog(null); }
    })();
    return () => { live = false; };
  }, [isAuthenticated, ensureAuth]);

  // the local styling per sku, priced by the server catalog
  const gifts = (catalog?.gifts || [])
    .map((g) => {
      const look = SKUS.find((s) => s.sku === g.sku);
      return look ? { ...look, usd: g.price_cents / 100 } : null;
    })
    .filter(Boolean);

  // Gifts are paid from the Lunexx balance: one request posts the gift and
  // charges it together (credits_send_gift). A fresh id per tap; the server
  // never charges the same id twice.
  const send = async (item) => {
    if (sending) return;

    setSending(item.sku);
    setErr(null);
    setNeedsFunds(null);

    try {
      if (!conversationId) {
        throw new Error('No active conversation');
      }
      const token = await ensureAuth();
      const giftId = crypto.randomUUID();
      await giftCall(token, { action: 'gift_buy', conversationId, giftId, sku: item.sku });
      balanceChanged();

      setBurst({
        ...item,
        key: Date.now(),
      });

      window.setTimeout(() => {
        setBurst(null);
      }, 1250);

      await onSent?.();
    } catch (error) {
      console.error('[GiftBar] Gift failed', { code: error?.code, conversationId, sku: item.sku });
      setErr(error?.message || 'The gift could not be sent.');
      if (error?.code === 'insufficient_funds') {
        setNeedsFunds(error);
        openAddFunds({ error, required_cents: Math.round(item.usd * 100), context: 'gift' });
      }
    } finally {
      setSending(null);
    }
  };

  if (!catalog?.available || !gifts.length) return null;

  return (
    <div className="relative mb-2">
      <AnimatePresence>
        {burst && (
          <GiftBurst
            key={burst.key}
            gift={burst}
          />
        )}
      </AnimatePresence>

      {/* Compact gift row — no heading */}
      <div className="-mt-2 flex w-full gap-1.5 overflow-x-auto px-0.5 pb-1 pt-2.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {gifts.map((gift) => {
          const isSending =
            sending === gift.sku;

          const anotherSending =
            Boolean(sending) && !isSending;

          return (
            <motion.button
              key={gift.sku}
              type="button"
              onClick={() => send(gift)}
              disabled={Boolean(sending)}
              aria-label={`Send ${gift.sku} gift for $${gift.usd}`}
              title={`${gift.emoji} · $${gift.usd}`}
              initial={false}
              animate={{
                opacity: anotherSending ? 0.38 : 1,
                scale: isSending ? 0.97 : 1,
              }}
              whileHover={
                sending
                  ? undefined
                  : {
                    y: -4,
                    scale: 1.055,
                    rotate: gift.rotation,
                  }
              }
              whileTap={
                sending
                  ? undefined
                  : {
                    scale: 0.9,
                    rotate: 0,
                  }
              }
              transition={{
                type: 'spring',
                stiffness: 390,
                damping: 23,
              }}
              className="group relative h-[62px] w-[58px] shrink-0 overflow-hidden rounded-[16px] border outline-none transition-colors focus-visible:ring-2 focus-visible:ring-white/30 disabled:cursor-wait"
              style={{
                borderColor: `rgba(${gift.accentRgb}, 0.22)`,
                background: `
                radial-gradient(
                  circle at 50% 5%,
                  rgba(${gift.accentRgb}, 0.24),
                  transparent 55%
                ),
                linear-gradient(
                  180deg,
                  rgba(255,255,255,0.075),
                  rgba(255,255,255,0.022)
                )
              `,
                boxShadow: `
                inset 0 1px 0 rgba(255,255,255,0.10),
                0 8px 20px rgba(0,0,0,0.22),
                0 0 16px rgba(${gift.accentRgb}, 0.07)
              `,
              }}
            >
              {/* Hover glow */}
              <div
                className="pointer-events-none absolute -inset-6 rounded-full opacity-0 blur-xl transition-all duration-300 group-hover:scale-125 group-hover:opacity-100"
                style={{
                  backgroundColor: `rgba(${gift.accentRgb}, 0.27)`,
                }}
              />

              {/* Top highlight */}
              <div className="pointer-events-none absolute inset-x-2.5 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />

              {/* Animated sheen */}
              <motion.div
                aria-hidden
                animate={{
                  x: ['-170%', '240%'],
                }}
                transition={{
                  duration: 2.8,
                  repeat: Infinity,
                  repeatDelay: 1.8,
                  ease: 'easeInOut',
                }}
                className="pointer-events-none absolute -top-7 h-24 w-6 rotate-[18deg] bg-gradient-to-r from-transparent via-white/[0.12] to-transparent blur-sm"
              />

              {/* Emoji glow */}
              <div
                className="pointer-events-none absolute left-1/2 top-[18px] h-8 w-8 -translate-x-1/2 rounded-full blur-lg transition-transform duration-300 group-hover:scale-125"
                style={{
                  backgroundColor: `rgba(${gift.accentRgb}, 0.38)`,
                  opacity: 0.72,
                }}
              />

              {/* Emoji */}
              <motion.div
                animate={
                  isSending
                    ? {
                      scale: [1, 1.17, 0.96, 1.1, 1],
                      rotate: [0, -8, 7, -4, 0],
                    }
                    : undefined
                }
                transition={
                  isSending
                    ? {
                      duration: 0.85,
                      repeat: Infinity,
                    }
                    : undefined
                }
                className="absolute inset-x-0 top-[7px] flex justify-center text-[25px] leading-none drop-shadow-[0_6px_10px_rgba(0,0,0,0.42)] transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:scale-110"
              >
                {isSending ? (
                  <div
                    className="mt-0.5 grid h-8 w-8 place-items-center rounded-full border bg-black/55 backdrop-blur"
                    style={{
                      borderColor: `rgba(${gift.accentRgb}, 0.4)`,
                      boxShadow: `0 0 20px rgba(${gift.accentRgb}, 0.35)`,
                    }}
                  >
                    <Loader2
                      size={14}
                      className="animate-spin"
                      style={{
                        color: gift.accent,
                      }}
                    />
                  </div>
                ) : (
                  gift.emoji
                )}
              </motion.div>

              {/* Price — separated from icon */}
              <div className="absolute inset-x-0 bottom-[10px] text-center leading-none">
                <span
                  className="font-mono text-[9px] font-bold"
                  style={{
                    color: gift.accent,
                    textShadow: `0 0 9px rgba(${gift.accentRgb}, 0.48)`,
                  }}
                >
                  ${gift.usd}
                </span>
              </div>

              {/* Active charging border */}
              {isSending && (
                <motion.div
                  aria-hidden
                  initial={{ opacity: 0 }}
                  animate={{
                    opacity: [0.35, 1, 0.35],
                  }}
                  transition={{
                    duration: 0.8,
                    repeat: Infinity,
                  }}
                  className="pointer-events-none absolute inset-0 rounded-[16px] border"
                  style={{
                    borderColor: gift.accent,
                    boxShadow: `
                    inset 0 0 18px rgba(${gift.accentRgb}, 0.18),
                    0 0 24px rgba(${gift.accentRgb}, 0.32)
                  `,
                  }}
                />
              )}
            </motion.button>
          );
        })}
      </div>

      <AnimatePresence>
        {err && (
          <motion.p
            initial={{
              opacity: 0,
              y: -3,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            exit={{
              opacity: 0,
              y: -3,
            }}
            className="flex items-center gap-2 px-0.5 pt-1 text-xs text-red-400/90"
          >
            <span>{err}</span>
            {needsFunds && (
              <AddFundsButton error={needsFunds} context="gift" />
            )}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

export const GiftBubble = ({
  message,
  isMe,
  resolvedGift,
}) => {
  const gift =
    resolvedGift ??
    resolveGiftMessage(message);

  if (!gift) {
    return null;
  }

  return (
    <div
      className="relative min-w-[205px] w-fit overflow-hidden rounded-[22px] border px-5 py-4"
      style={{
        borderColor: `rgba(${gift.accentRgb}, 0.26)`,
        background: `
          radial-gradient(
            circle at 50% 0%,
            rgba(${gift.accentRgb}, 0.22),
            transparent 58%
          ),
          linear-gradient(
            180deg,
            rgba(255,255,255,0.07),
            rgba(255,255,255,0.025)
          )
        `,
        boxShadow: `
          inset 0 1px 0 rgba(255,255,255,0.10),
          0 14px 32px rgba(0,0,0,0.28),
          0 0 24px rgba(${gift.accentRgb}, 0.10)
        `,
      }}
    >
      <div
        className="pointer-events-none absolute left-1/2 top-2 h-16 w-16 -translate-x-1/2 rounded-full blur-2xl"
        style={{
          backgroundColor:
            `rgba(${gift.accentRgb}, 0.25)`,
        }}
      />

      <div className="relative flex flex-col items-center text-center">
        <div
          className="grid h-12 w-12 place-items-center rounded-[16px] border text-[28px]"
          style={{
            borderColor:
              `rgba(${gift.accentRgb}, 0.28)`,
            backgroundColor:
              `rgba(${gift.accentRgb}, 0.10)`,
            boxShadow:
              `0 0 20px rgba(${gift.accentRgb}, 0.16)`,
          }}
        >
          {gift.emoji}
        </div>

        <p className="mt-3 text-[13px] font-semibold tracking-tight text-white">
          {isMe
            ? 'You sent a gift'
            : 'You received a gift'}
        </p>

        <p
          className="mt-1 font-mono text-[10px] font-bold"
          style={{
            color: gift.accent,
            textShadow:
              `0 0 10px rgba(${gift.accentRgb}, 0.42)`,
          }}
        >
          ${gift.usd} activated
        </p>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------
// UNIFIED PAYWALL (redesign): every creator gives 10 free messages; the
// wall after is ALWAYS "hold the pass → unlimited chat". AI creators
// additionally show the rent tier (Chat Pass — $10 / 300 messages, this
// chat only). Whether it appears is decided SERVER-side via
// status.rent.available and must never be re-derived here: post-curve it
// is every wall, and while MARKETS_ENABLED it is the old "pass costs
// more than 0.03 ETH" rule.

const formatEthPrice = (wei) => {
  try {
    if (wei == null) return null;
    const eth = Number(BigInt(wei)) / 1e18;
    if (!Number.isFinite(eth) || eth <= 0) return null;
    return eth < 0.0001 ? '<0.0001' : eth.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
  } catch { return null; }
};

/** THE white bar — identical for human and AI creators. Routes to the
 *  creator's Market tab, with copy matching whatever is actually for sale.
 *
 *  Post-curve there are three distinct states, and collapsing them would
 *  produce the worst kind of paywall: one that asks for money for something
 *  that cannot be bought. A drop that has not happened, and a drop that has
 *  sold out, are NOT the same as a drop with passes left, so each gets its
 *  own honest sentence and only the buyable one shows a price. */
function OwnXPassBar({ status }) {
  const navigate = useNavigate();
  const go = () => navigate(`/kol/${status?.handle}?tab=market`);

  // Legacy curve copy — unchanged while MARKETS_ENABLED.
  if (MARKETS_ENABLED) {
    const priceEth = formatEthPrice(status?.xpass_price_wei);
    return (
      <button
        onClick={go}
        className="flex w-full items-center justify-between gap-3 rounded-xl bg-white px-4 py-3 text-left transition hover:bg-white/90"
      >
        <span>
          <span className="block text-sm font-bold text-black">
            Get {status?.name}'s XPass{priceEth ? ` · ${priceEth} ETH` : ''}
          </span>
          <span className="block text-[11px] font-medium text-black/60">
            Unlimited chat · free livestreams · sellable anytime
          </span>
        </span>
      </button>
    );
  }

  const card = status?.card ?? null;
  const dropLive = card?.dropLive === true && card?.seriesId != null;
  const soldOut = card?.soldOut === true;
  const priceEth = formatEthPrice(card?.priceWei);

  // No drop yet: there is nothing to sell, so don't imply there is.
  if (!dropLive) {
    return (
      <div className="rounded-xl border border-white/[0.08] bg-[#181818] px-4 py-3 shadow-inner">
        <span className="block text-sm font-bold text-white">
          {status?.name}'s founding passes haven't dropped yet
        </span>
        <span className="block text-[11px] font-medium text-zinc-500">
          100 will be released, once. Follow to hear when.
        </span>
      </div>
    );
  }

  // Sold out: the mint is closed forever, but the secondary market is not —
  // send them somewhere that can actually be acted on.
  if (soldOut) {
    return (
      <button
        onClick={go}
        className="flex w-full items-center justify-between gap-3 rounded-xl border border-white/[0.08] bg-[#181818] px-4 py-3 text-left shadow-inner transition hover:bg-[#1D1D1D]"
      >
        <span>
          <span className="block text-sm font-bold text-white">
            All {card?.maxSupply ?? 100} founding passes are claimed
          </span>
          <span className="block text-[11px] font-medium text-zinc-500">
            See if a holder is reselling one
          </span>
        </span>
      </button>
    );
  }

  const left = Math.max(0, (card?.maxSupply ?? 100) - (card?.minted ?? 0));
  return (
    <button
      onClick={go}
      className="flex w-full items-center justify-between gap-3 rounded-xl bg-white px-4 py-3 text-left transition hover:bg-white/90"
    >
      <span>
        <span className="block text-sm font-bold text-black">
          Get {status?.name}'s Founding Pass{priceEth ? ` · ${priceEth} ETH` : ''}
        </span>
        <span className="block text-[11px] font-medium text-black/60">
          Unlimited chat · free rooms · 10% credit back{left ? ` · ${left} of ${card?.maxSupply ?? 100} left` : ''}
        </span>
      </span>
    </button>
  );
}

/** DEPRECATED ALIAS. Human creators used to get their own composer
 *  replacement with no rent tier; they now get the same paywall as
 *  everyone else. Kept so existing imports keep working — prefer
 *  <ChatPaywall>. */
export function HumanChatGate(props) {
  return <ChatPaywall {...props} />;
}

/** THE paywall after the free taste, for BOTH creator types: Own the
 *  founding pass (always) / Rent a Chat Pass (whenever the server offers
 *  it, which post-curve is every wall).
 *
 *  Was AI-only. It covers human creators too now that the separate 30-day
 *  membership is retiring and the card IS the membership — leaving a human
 *  creator whose drop has not happened or has sold out with an Own bar and
 *  nothing else would be a wall with nothing purchasable behind it.
 *  Whether the Rent half appears is still the SERVER's call via
 *  status.rent.available; this component never infers it from the creator
 *  type. */
export function ChatPaywall({ status, creatorId, onPurchased }) {
  const { ensureAuth } = useSiweAuth();
  const [buying, setBuying] = useState(false);
  const [err, setErr] = useState(null);
  const [passNeedsTopUp, setPassNeedsTopUp] = useState(false);

  const rentAvailable = status?.rent?.available === true;
  const used = status?.credits ? status.credits.turns_used : null;

  const buyChatPass = async () => {
    setBuying(true); setErr(null); setPassNeedsTopUp(false);
    try {
      const token = await ensureAuth();
      const quote = await bridge(token, { action: 'chat_pass_quote' });
      await payThenVerify(ensureAuth, PLATFORM_WALLET, quote.eth, {
        action: 'chat_pass_buy', creatorId,
      });
      onPurchased?.();
    } catch (e) {
      const c = classifyTxError(e);
      setErr(c.message);
      setPassNeedsTopUp(c.topUp);
    }
    finally { setBuying(false); }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#141414] px-5 py-4">
      <div className="mb-3 flex items-center gap-3">
        <Sparkles size={16} className="shrink-0 text-white/40" />
        <p className="min-w-0 text-sm text-white/60">
          {used
            ? `You've chatted your ${used} free messages with ${status?.name}`
            : `Keep chatting with ${status?.name}`}
        </p>
      </div>

      <div className={`grid gap-2 ${rentAvailable ? 'sm:grid-cols-2' : ''}`}>
        {/* OWN — always the primary path; the SAME white bar humans see */}
        <OwnXPassBar status={status} />

        {/* RENT — the Chat Pass. Server decides (status.rent.available);
            post-curve that is every wall, so this is the path that keeps
            the paywall from being a dead end before a drop or after a
            sell-out. Never re-implement the rule here. */}
        {rentAvailable && (
          <button
            onClick={buyChatPass}
            disabled={buying}
            className="flex items-center justify-between gap-3 rounded-xl border border-white/15 px-4 py-3 text-left transition hover:border-white/30 disabled:opacity-50"
          >
            <span>
              <span className="flex items-center gap-2 text-sm font-semibold text-white">
                {buying && <Loader2 size={13} className="animate-spin" />}
                Chat Pass · ${status?.rent?.usd ?? 10}
              </span>
              <span className="block text-[11px] text-white/40">
                {status?.rent?.turns ?? 300} messages · this chat only
              </span>
            </span>
          </button>
        )}
      </div>

      {err && (
        <div className="mt-2 flex items-center gap-2">
          <p className="text-xs text-red-400/90">{err}</p>
          {passNeedsTopUp && <TopUpCta />}
        </div>
      )}
    </div>
  );
}